import { Link } from 'react-router-dom';
import type { QueueMaxLimit } from '@kaeliux/game-engine';
import { RESOURCE_NAMES } from '@kaeliux/shared';
import type { QueueMaxPreview } from '../hooks/usePlanetBuildActions';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';
import { linkButtonClasses } from './ui/buttonStyles';
import { CostChips } from './planet/CostChips';
import { cn } from '../utils/cn';

const LIMIT_LABEL: Record<QueueMaxLimit, string> = {
    queue: 'queue slots',
    'max-level': 'max level',
    prerequisites: 'requirements',
    metal: RESOURCE_NAMES.metal,
    minerals: RESOURCE_NAMES.minerals,
    gas: RESOURCE_NAMES.gas,
    energy: RESOURCE_NAMES.energy,
    plot: 'free plots',
    requested: 'the levels shown',
};

export interface QueueMaxButtonProps {
    preview: QueueMaxPreview;
    loading: boolean;
    disabled: boolean;
    onQueue: () => void;
    /** Stretch to the container width (the 268px popover) instead of sizing to the label. */
    fullWidth?: boolean;
}

/**
 * The Platinum "Queue max" building action (issue #219): one click queues
 * every level that fits right now. Below Platinum it renders as a locked
 * link to the premium page instead. Shared by the building popover and the
 * zone list so both read and behave identically.
 */
export function QueueMaxButton({ preview, loading, disabled, onQueue, fullWidth = false }: QueueMaxButtonProps) {
    if (!preview.visible) return null;

    if (preview.access === 'locked') {
        return (
            <Link
                to="/premium"
                className={cn(linkButtonClasses('ghost'), fullWidth && 'w-full')}
                data-testid="queue-max-locked"
                title="Queue every level that fits in one click — a Platinum feature"
            >
                <Icon name="lock" size={14} />
                Queue {preview.count} levels · Platinum
            </Link>
        );
    }

    return (
        <div className={cn('flex flex-col gap-1.5', fullWidth ? 'w-full' : 'items-start sm:items-end')} data-testid="queue-max">
            <Button
                variant="secondary"
                size="sm"
                fullWidth={fullWidth}
                loading={loading}
                disabled={disabled}
                onClick={onQueue}
                data-testid="queue-max-button"
            >
                Queue {preview.count} levels
            </Button>
            <CostChips cost={preview.totalCost} testIdPrefix="queue-max-cost" />
            <span className="text-micro text-content-muted" data-testid="queue-max-limit">
                Limited by: {LIMIT_LABEL[preview.limitedBy]}
            </span>
        </div>
    );
}

export default QueueMaxButton;
